import React, { useCallback, useId, useState } from "react"
import { Repeater, types, useAdminContext } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../Typography"

import { BackgroundColor } from "../components/layout"
import Divider from "../decorative/Divider"
import { Icon } from "../components/icon"
import { Icons } from "../../types"
import classNames from "classnames"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"
import { useMediaQuery } from "usehooks-ts"

interface FAQProps {
    textVisible: TypographyElement[]
    textHidden: TypographyElement[]
    index?: number
    bgColor?: BackgroundColor
    setIsOpen: (index?: number | ((isOpen: number) => number | undefined)) => void
    isOpen: number | undefined
}

const FAQ: types.Brick<FAQProps> = ({ index, setIsOpen, isOpen, textHidden }) => {
    const [isOpenMobile, setIsOpenMobile] = useState(index === isOpen)

    const isMobile = useMediaQuery("(max-width: 767px")

    const open = useCallback(
        (index?: number) => (isMobile ? isOpenMobile : isOpen === index),
        [isMobile, isOpen, isOpenMobile]
    )

    const triggerId = useId()
    const contentId = useId()

    const emptyTextHidden = useCheckForEmptyText(textHidden)
    const { isAdmin } = useAdminContext()

    return (
        <li key={index}>
            <button
                type="button"
                onClick={() => {
                    setIsOpen((isOpen: number) => (index === isOpen ? undefined : index))
                    setIsOpenMobile(isOpenMobile => !isOpenMobile)
                }}
                className="flex w-full justify-between gap-5 text-left font-gibson"
                id={triggerId}
                aria-expanded={open(index)}
                aria-controls={contentId}
            >
                <Typography propName="textVisible" allowList={["bold"]} />
                <div>
                    <Icon
                        icon={Icons.arrow_down}
                        className={classNames("transition duration-300 ease-in-out", { "rotate-180": open(index) })}
                    />
                </div>
            </button>
            <div
                className={classNames(
                    "grid transition-grid-template-rows duration-300 ease-out",
                    open(index) ? "grid-rows-1fr" : "grid-rows-0fr"
                )}
                id={contentId}
                aria-labelledby={triggerId}
                aria-hidden={!open(index)}
            >
                <div className={classNames({ "overflow-hidden": !(isAdmin && open(index)) })}>
                    <div className="mt-5 space-y-7 sm:mt-7.5 md:space-y-10 xl:pr-12">
                        {!emptyTextHidden && (
                            <Typography
                                allowList={["bold", "sup", "link", "small", "checkList", "ul"]}
                                propName="textHidden"
                                hidden={!open(index)}
                            />
                        )}
                        <Repeater propName="repeater" itemProps={{ inline: true }} />
                    </div>
                </div>
            </div>
            <div className="mb-7 mt-5 sm:my-10">
                <Divider spacerTop={false} spacerBottom={false} />
            </div>
        </li>
    )
}

FAQ.schema = {
    name: "FAQ",
    label: "FAQ",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Table",
                    label: "Table",
                    max: 1,
                },
            ],
        },
    ],
    getDefaultProps: (): Omit<FAQProps, "setIsOpen" | "isOpen" | "id"> => ({
        textVisible: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet.",
                        bold: true,
                    },
                ],
            },
        ],
        textHidden: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.",
                    },
                ],
            },
        ],
    }),
}

export default FAQ
